import { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ClientOnly, Head } from 'vite-react-ssg'
import { AuthProvider } from './auth/AuthProvider'
import { useAuth } from './auth/useAuth'
import { CartProvider } from './providers/CartProvider'
import { BrandingProvider } from './branding/BrandingProvider'
import { BrandHead } from './branding/BrandHead'
import { setAuthTokenProvider } from './api/client'
import { ConsentBanner } from './tracking/ConsentBanner'
import { loadGtm } from './tracking/gtm'
import { getStoredConsent, updateConsent } from './tracking/consent'

function AuthTokenBridge() {
  const { getAccessToken } = useAuth()
  useEffect(() => {
    setAuthTokenProvider(getAccessToken)
  }, [getAccessToken])
  return null
}

/**
 * Root route element wrapping every page with the global providers (exactly as
 * the old main.tsx did) plus the GTM / consent bootstrap. Renders <Outlet /> so
 * each route's page mounts underneath. SSG-safe: all browser work is in effects.
 */
export function RootLayout() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 3,
            retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
          },
        },
      }),
  )

  // Load GTM once and re-apply a previously granted consent choice.
  useEffect(() => {
    loadGtm()
    if (getStoredConsent() === 'accepted') updateConsent(true)
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      {/* Default head — overridden per page by <Seo>. Keeps non-prerendered
          (app/auth) routes from rendering with an empty <title>. */}
      <Head>
        <title>Zenzers — Geriatric Remote Monitoring &amp; Fall Detection</title>
        <meta
          name="description"
          content="Geriatric remote monitoring by Zenzers: continuous vitals, AI fall detection, and instant caregiver alerts — privacy-first, processed on the edge."
        />
      </Head>
      <BrandingProvider>
        {/* Brand-aware default title/OG — overrides the static Zenzers <Head> above
            once the per-domain config resolves. Per-page <Seo> still wins over this. */}
        <BrandHead />
        <AuthProvider>
          <AuthTokenBridge />
          <CartProvider>
            <Outlet />
            <ClientOnly>{() => <ConsentBanner />}</ClientOnly>
          </CartProvider>
        </AuthProvider>
      </BrandingProvider>
    </QueryClientProvider>
  )
}
